import { useId, useMemo, useState } from "react";

const initialMonth = () => new Date().toISOString().slice(0, 7);

export const parseExtraValueCents = (value) => {
  const text = String(value ?? "").trim();
  const normalized = text.includes(",")
    ? text.replace(/\./g, "").replace(",", ".")
    : text;
  const amount = Number(normalized);
  const cents = Math.round(amount * 100);
  if (
    !Number.isFinite(amount) ||
    !Number.isSafeInteger(cents) ||
    cents === 0 ||
    cents / 100 !== amount
  ) {
    return null;
  }
  return Math.abs(cents);
};

const ExtraValueForm = ({ target, onSubmit, onCancel }) => {
  const id = useId();
  const [fields, setFields] = useState({
    type: "expense",
    value: "",
    description: "",
    competence: initialMonth(),
    installments: "1",
  });
  const [status, setStatus] = useState({ kind: "idle", message: "" });
  const [invalidFields, setInvalidFields] = useState(new Set());
  const update = (field) => (event) => {
    const { value } = event.target;
    setFields((current) => ({ ...current, [field]: value }));
    setInvalidFields((current) => {
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };

  const targetDescription =
    target?.description ?? target?.wbs_description ?? target?.wbs_code_id;
  const targetCode = target?.wbs ?? target?.code ?? target?.wbs_code_id;
  const preview = useMemo(() => {
    const cents = parseExtraValueCents(fields.value);
    if (!cents) return null;
    const formatted = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
    return {
      value: `${fields.type === "saving" ? "−" : "+"}${formatted}`,
      label:
        fields.type === "saving" ? "Redução prevista" : "Acréscimo previsto",
    };
  }, [fields.type, fields.value]);

  const submit = async (event) => {
    event.preventDefault();
    const cents = parseExtraValueCents(fields.value);
    const installments = Number(fields.installments);
    const invalid = new Set();
    if (!cents) invalid.add("value");
    if (!fields.description.trim()) invalid.add("description");
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(fields.competence)) {
      invalid.add("competence");
    }
    if (
      !Number.isSafeInteger(installments) ||
      installments < 1 ||
      installments > 36
    ) {
      invalid.add("installments");
    }
    if (invalid.size) {
      setInvalidFields(invalid);
      setStatus({
        kind: "error",
        message: "Revise os campos destacados antes de salvar.",
      });
      return;
    }
    setInvalidFields(new Set());
    setStatus({ kind: "saving", message: "Salvando…" });
    const signedCents = fields.type === "saving" ? -cents : cents;
    const result = await onSubmit?.({
      value: signedCents / 100,
      totalCents: signedCents,
      installments,
      start_date: fields.competence,
      isValid: true,
      description: fields.description,
    });
    if (result?.ok) {
      setStatus({ kind: "success", message: "Lançamento criado." });
      return;
    }
    const typed = {
      extra_value_target_level_mismatch:
        "Este alvo não está no nível de apropriação da obra.",
      extra_value_appropriation_level_unknown:
        "O nível de apropriação da obra precisa ser corrigido.",
      extra_value_conflict:
        "Os dados mudaram. Recarregue o alvo e tente novamente.",
    }[result?.errorCode];
    setStatus({
      kind: "error",
      message:
        typed ?? result?.message ?? "Não foi possível criar o lançamento.",
    });
  };

  return (
    <form
      className="extra-value-form"
      aria-labelledby={`${id}-title`}
      onSubmit={submit}
      noValidate
    >
      <div className="extra-value-form__intro">
        <span className="extra-value-form__eyebrow">Alvo selecionado</span>
        <h3 id={`${id}-title`}>{targetDescription}</h3>
        {targetCode ? <small>{targetCode}</small> : null}
      </div>

      <fieldset className="extra-value-form__type">
        <legend>Tipo de lançamento</legend>
        <div className="extra-value-form__type-options">
          <label className={fields.type === "expense" ? "is-selected" : ""}>
            <input
              type="radio"
              name={`${id}-type`}
              value="expense"
              checked={fields.type === "expense"}
              onChange={update("type")}
            />
            <span>
              <strong>Estouro</strong>
              <small>Aumenta o custo previsto</small>
            </span>
          </label>
          <label className={fields.type === "saving" ? "is-selected" : ""}>
            <input
              type="radio"
              name={`${id}-type`}
              value="saving"
              checked={fields.type === "saving"}
              onChange={update("type")}
            />
            <span>
              <strong>Economia</strong>
              <small>Reduz o custo previsto</small>
            </span>
          </label>
        </div>
      </fieldset>

      <div className="extra-value-form__section">
        <div className="extra-value-form__field">
          <label htmlFor={`${id}-value`}>Valor total</label>
          <div className="extra-value-form__currency">
            <span aria-hidden="true">R$</span>
            <input
              id={`${id}-value`}
              inputMode="decimal"
              placeholder="0,00"
              aria-invalid={invalidFields.has("value")}
              aria-describedby={`${id}-value-help`}
              value={fields.value}
              onChange={update("value")}
            />
          </div>
          <small id={`${id}-value-help`}>
            Informe o total; o sinal é definido pelo tipo.
          </small>
        </div>
        <div className="extra-value-form__field">
          <label htmlFor={`${id}-description`}>Descrição</label>
          <textarea
            id={`${id}-description`}
            rows="3"
            maxLength="240"
            placeholder="Explique o motivo do lançamento"
            aria-invalid={invalidFields.has("description")}
            value={fields.description}
            onChange={update("description")}
          />
          <small>{fields.description.length}/240 caracteres</small>
        </div>
        <div className="extra-value-form__schedule">
          <div className="extra-value-form__field">
            <label htmlFor={`${id}-competence`}>Competência inicial</label>
            <input
              id={`${id}-competence`}
              type="month"
              aria-invalid={invalidFields.has("competence")}
              value={fields.competence}
              onChange={update("competence")}
            />
          </div>
          <div className="extra-value-form__field">
            <label htmlFor={`${id}-installments`}>Parcelas</label>
            <input
              id={`${id}-installments`}
              type="number"
              min="1"
              max="36"
              aria-invalid={invalidFields.has("installments")}
              value={fields.installments}
              onChange={update("installments")}
            />
          </div>
        </div>
      </div>

      {preview ? (
        <div
          className={`extra-value-form__preview extra-value-form__preview--${fields.type}`}
          aria-live="polite"
        >
          <span>{preview.label}</span>
          <strong>{preview.value}</strong>
          <small>
            {fields.installments || 1}{" "}
            {Number(fields.installments) === 1 ? "parcela" : "parcelas"}
          </small>
        </div>
      ) : null}

      {status.kind !== "idle" ? (
        <p
          className={`extra-value-form__status extra-value-form__status--${status.kind}`}
          role={status.kind === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {status.message}
        </p>
      ) : null}
      <div className="extra-value-form__actions">
        <button
          type="button"
          className="button button-secondary"
          onClick={onCancel}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="button button-accent"
          disabled={status.kind === "saving"}
        >
          {status.kind === "saving" ? "Salvando…" : "Salvar lançamento"}
        </button>
      </div>
    </form>
  );
};

export default ExtraValueForm;
